// src/components/calendar/TasksRail.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
function getChecklistStatus(v) {
  return (v || 'PENDING').toUpperCase();
}

function isChecklistDone(v) {
  const st = getChecklistStatus(v);
  return st === 'COMPLETED' || st === 'NOT_REQUIRED';
}

function relTime(d, now) {
  try {
    const s = formatDistanceToNowStrict(d, { addSuffix: true, baseDate: now });
    return s.replace(/^about\s+/i, '');
  } catch {
    return '';
  }
}

/**
 * Parse AWSDateTime into a true instant (Date).
 * - If value has Z or +/-hh:mm => treat as instant
 * - If no timezone => treat as wall-clock in tz, convert to instant
 */
function parseAwsDateTimeToInstant(value, tz) {
  if (!value) return null;

  if (value instanceof Date) {
    // Treat it as an instant only if it came from an ISO with timezone (common case: stored UTC)
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const s = String(value).trim();
  if (!s) return null;

  const hasTZ = /([zZ]|[+-]\d{2}:\d{2})$/.test(s);

  const d = hasTZ ? new Date(s) : fromZonedTime(s, tz);

  return Number.isNaN(d.getTime()) ? null : d;
}


/**
 * Props:
 * - events
 * - carMap
 * - visibleStart
 * - visibleEnd
 * - onOpen(event)
 * - canRemoveAssignments
 * - onRemoveAssignment(taskRow)
 * - tz (IANA timezone, e.g. "Asia/Tbilisi")
 */
export default function TasksRail({
  events,
  carMap,
  visibleStart,
  visibleEnd,
  onOpen,
  canRemoveAssignments = false,
  onRemoveAssignment,
  tz,
}) {
  const safeTz = tz || 'UTC';
  if (!tz) console.warn('TasksRail: missing tz prop, using UTC fallback');

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const [page, setPage] = useState(1);
  const pageSize = 7;

  const tasks = useMemo(() => {
    if (!Array.isArray(events)) return [];

    const rows = [];
    const seenReservationIds = new Set();

    for (const ev of events) {
      const r = ev.resource || {};
      const reservationId = String(r.id || '');
      const carName = r.carId ? carMap.get(r.carId) || '-' : ev.title || '-';
      const isMaint = r.__kind === 'maint';
      if (!isMaint && reservationId) {
        if (seenReservationIds.has(reservationId)) continue;
        seenReservationIds.add(reservationId);
      }

      // === Maintenance ===
      if (isMaint) {
        // ev.start is already a Date from CalendarPage; normalize to tz
        const at = parseAwsDateTimeToInstant(ev.start, safeTz);
        if (at && !Number.isNaN(at.getTime())) {
          rows.push({
            kind: 'Maintenance',
            at,
            carName,
            where: '',
            who: '',
            ref: '',
            checklistStatus: null,
            ev,
          });
        }
        continue;
      }

      // === PICKUP TASK ===
      const pickupAtRaw = r.pickupAt || r.start;
      if (pickupAtRaw) {
        const pickupAt = parseAwsDateTimeToInstant(pickupAtRaw, safeTz);
        if (pickupAt && !Number.isNaN(pickupAt.getTime())) {
          const done = isChecklistDone(r.pickupChecklistStatus);
          if (!done) {
            rows.push({
              kind: 'Pickup',
              at: pickupAt,
              carName,
              where: r.pickupLocation || '',
              who: r.customerName || '',
              ref: r.reservationRef || '',
              checklistStatus: r.pickupChecklistStatus,
              ev,
            });
          }
        }
      }

      // === DROPOFF TASK ===
      const dropoffAtRaw = r.dropoffAt || r.end;
      if (dropoffAtRaw) {
        const dropoffAt = parseAwsDateTimeToInstant(dropoffAtRaw, safeTz);
        if (dropoffAt && !Number.isNaN(dropoffAt.getTime())) {
          const done = isChecklistDone(r.dropoffChecklistStatus);
          if (!done) {
            rows.push({
              kind: 'Dropoff',
              at: dropoffAt,
              carName,
              where: r.dropoffLocation || '',
              who: r.customerName || '',
              ref: r.reservationRef || '',
              checklistStatus: r.dropoffChecklistStatus,
              ev,
            });
          }
        }
      }
    }

    rows.sort((a, b) => a.at - b.at);

    // visibleStart/End are passed from CalendarPage in the SAME zoned space
    const floor = visibleStart ?? null;
    const ceiling = visibleEnd ?? null;

    return rows.filter((t) => {
      if (floor && t.at < floor) return false;
      if (ceiling && t.at > ceiling) return false;
      return true;
    });
  }, [events, carMap, visibleStart, visibleEnd, safeTz]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(tasks.length / pageSize));
    setPage((prev) => {
      if (prev < 1) return 1;
      if (prev > totalPages) return totalPages;
      return prev;
    });
  }, [tasks]);

  const totalPages = Math.max(1, Math.ceil(tasks.length / pageSize));
  const visibleTasks = useMemo(() => {
    if (!tasks.length) return [];
    const startIndex = (page - 1) * pageSize;
    return tasks.slice(startIndex, startIndex + pageSize);
  }, [tasks, page]);
  const baseCardStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 10,
    cursor: 'pointer',
    userSelect: 'none',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
  };

  const removeBtnStyle = {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    padding: '2px 6px',
    color: '#6b7280',
    borderRadius: 999,
  };

  const pillStyle = (kind, overdue) => {
    const base = {
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: 999,
      border: '1px solid #e5e7eb',
      whiteSpace: 'nowrap',
    };

    if (overdue) return { ...base, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' };
    if (kind === 'Maintenance') return { ...base, background: '#111827', borderColor: '#111827', color: '#ffffff' };
    if (kind === 'Pickup') return { ...base, background: '#ecfeff', borderColor: '#cffafe', color: '#155e75' };
    if (kind === 'Dropoff') return { ...base, background: '#eef2ff', borderColor: '#e0e7ff', color: '#3730a3' };
    return { ...base, background: '#f3f4f6', color: '#374151' };
  };


  const pagerButtonStyle = {
    border: '1px solid #d1d5db',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 11,
    background: '#ffffff',
    cursor: 'pointer',
  };

  const pagerButtonDisabledStyle = {
    ...pagerButtonStyle,
    opacity: 0.5,
    cursor: 'default',
  };

  const startItem = tasks.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem =
    tasks.length === 0
      ? 0
      : Math.min(page * pageSize, tasks.length);

  return (
    <aside
      style={{
        padding: 12,
        overflowY: 'auto',
        background: 'transparent',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >

      <h4 style={{ marginTop: 4, marginBottom: 2, fontSize: 14 }}>
        Upcoming tasks
      </h4>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>
        {tasks.length === 0
          ? '(No upcoming items)'
          : `Showing ${startItem}-${endItem} of ${tasks.length}`}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {visibleTasks.map((t, i) => {
          const isFormTask = t.kind === 'Pickup' || t.kind === 'Dropoff';
          const done = isFormTask ? isChecklistDone(t.checklistStatus) : false;
          const overdue = isFormTask && !done && t.at < now;

          const cardStyle = {
            ...baseCardStyle,
            border: overdue ? '1px solid #f97373' : baseCardStyle.border,
            background: overdue ? '#fef2f2' : baseCardStyle.background,
          };

          return (
            <div
              key={`${t.kind}-${t.ref || ''}-${t.at.toISOString()}-${i}`}
              role="button"
              tabIndex={0}
              style={cardStyle}
              onClick={() => onOpen?.(t.ev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen?.(t.ev);
                }
              }}
            >
              {/* header row: title + optional X */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 4,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={pillStyle(t.kind, overdue)}>{t.kind.toUpperCase()}</span>
                    <div style={{ fontWeight: 650, fontSize: 13 }}>
                      {formatInTimeZone(t.at, safeTz, 'EEE, MMM d HH:mm')}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {relTime(t.at, now)}
                    </div>
                  </div>

                  <div style={{ fontSize: 12, color: '#111827', marginTop: 2 }}>
                    {t.carName}
                  </div>

                </div>

                {/* X only for Pickup/Dropoff + allowed roles */}
                {isFormTask &&
                  canRemoveAssignments &&
                  typeof onRemoveAssignment === 'function' && (
                    <button
                      type="button"
                      aria-label="Remove task"
                      title="Hide this task (mark as not required)"
                      style={removeBtnStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f9fafb';
                        e.currentTarget.style.borderColor = '#d1d5db';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // don't open drawer
                        e.preventDefault();
                        onRemoveAssignment(t);
                      }}
                    >
                      x
                    </button>

                  )}
              </div>

              {(t.where || t.who || t.ref) && (
                <div
                  style={{
                    fontSize: 12,
                    color: '#4b5563',
                    marginTop: 4,
                    wordBreak: 'break-word',
                  }}
                >
                  {t.where && (
                    <>
                      <b>Location:</b> {t.where}
                      <br />
                    </>
                  )}
                  {t.who && (
                    <>
                      <b>Customer:</b> {t.who}
                      <br />
                    </>
                  )}
                  {t.ref && (
                    <>
                      <b>Ref:</b> {t.ref}
                    </>
                  )}

                </div>
              )}
              {overdue && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: '#b91c1c',
                  }}
                >
                  Overdue - form not submitted
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination controls */}
      {tasks.length > pageSize && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            color: '#6b7280',
          }}
        >
          <span>
            Page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              style={page > 1 ? pagerButtonStyle : pagerButtonDisabledStyle}
              onClick={() => {
                if (page <= 1) return;
                setPage((p) => p - 1);
              }}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              type="button"
              style={page < totalPages ? pagerButtonStyle : pagerButtonDisabledStyle}
              onClick={() => {
                if (page >= totalPages) return;
                setPage((p) => p + 1);
              }}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
